/**
 * Option groups in Loro (spec §7.6.1): alternatives among one block's own links and how many of them
 * may be chosen. `members` and `chosen` are movable lists of link ids, so concurrent choices both
 * survive the merge and SHACL reports a group chosen past its `maxChoice` (§17.2).
 */
import { LoroMap, LoroMovableList, type LoroDoc, type TreeID } from "loro-crdt";
import { v7 as uuidv7 } from "uuid";

export interface OptionGroup {
  id: string;
  label: string | null;
  members: string[];
  chosen: string[];
  minChoice: number;
  maxChoice: number | null;
}

export type CardinalityPreset = "exactlyOne" | "atLeastOne" | "any" | "custom";

export const PRESETS: Record<Exclude<CardinalityPreset, "custom">, { min: number; max: number | null; label: string }> = {
  exactlyOne: { min: 1, max: 1, label: "exactly one" },
  atLeastOne: { min: 1, max: null, label: "at least one" },
  any: { min: 0, max: null, label: "any combination" },
};

/**
 * The cardinality as the reader sees it (spec §8.23): a catalogue key and its parameters.
 * `describeCardinality` below stays as it is — it is the language-neutral form the `data-cardinality`
 * attribute carries, which the e2e suite reads.
 */
export function cardinalityMessage(min: number, max: number | null): { key: string; params?: Record<string, number> } {
  if (min === 1 && max === 1) return { key: "links.group.exactlyOne" };
  if (min === 1 && max === null) return { key: "links.group.atLeastOne" };
  if (min === 0 && max === null) return { key: "links.group.any" };
  if (max === null) return { key: "links.group.orMore", params: { min } };
  return min === max ? { key: "links.group.exactlyN", params: { n: min } } : { key: "links.group.range", params: { min, max } };
}

/** "exactly one", "at least one", "any combination", or "2..3" / "2 or more". */
export function describeCardinality(min: number, max: number | null): string {
  for (const p of Object.values(PRESETS)) if (p.min === min && p.max === max) return p.label;
  if (max === null) return `${min} or more`;
  return min === max ? `exactly ${min}` : `${min}..${max}`;
}

function node(doc: LoroDoc, treeId: TreeID) {
  return doc.getTree("blocks").getNodeByID(treeId);
}

function groupsMap(doc: LoroDoc, treeId: TreeID, create: boolean): LoroMap | null {
  const n = node(doc, treeId);
  if (!n) return null;
  const g = n.data.get("groups");
  if (g instanceof LoroMap) return g;
  return create ? n.data.setContainer("groups", new LoroMap()) : null;
}

function list(m: LoroMap, key: string): LoroMovableList {
  const l = m.get(key);
  return l instanceof LoroMovableList ? l : m.setContainer(key, new LoroMovableList());
}

function ids(v: unknown): string[] {
  return v instanceof LoroMovableList ? [...new Set(v.toArray().filter((x): x is string => typeof x === "string"))] : [];
}

export function readGroups(doc: LoroDoc, treeId: TreeID): OptionGroup[] {
  const groups = groupsMap(doc, treeId, false);
  if (!groups) return [];
  const out: OptionGroup[] = [];
  for (const id of groups.keys()) {
    const g = groups.get(id);
    if (!(g instanceof LoroMap)) continue;
    const max = g.get("maxChoice");
    const min = g.get("minChoice");
    const label = g.get("label");
    out.push({
      id,
      label: typeof label === "string" && label ? label : null,
      members: ids(g.get("members")),
      chosen: ids(g.get("chosen")),
      minChoice: typeof min === "number" && min >= 0 ? min : 0,
      maxChoice: typeof max === "number" && max >= 0 ? max : null,
    });
  }
  return out.sort((a, b) => a.id.localeCompare(b.id));
}

export function createGroup(
  doc: LoroDoc,
  treeId: TreeID,
  members: string[],
  opts: { label?: string | null; minChoice: number; maxChoice: number | null },
  userId: string,
): string {
  const groups = groupsMap(doc, treeId, true);
  if (!groups) throw new Error("block not found");
  const id = uuidv7();
  const g = groups.setContainer(id, new LoroMap());
  g.set("label", opts.label?.trim() || null);
  const m = g.setContainer("members", new LoroMovableList());
  [...new Set(members)].forEach((x) => m.push(x));
  g.setContainer("chosen", new LoroMovableList());
  g.set("minChoice", Math.max(0, Math.floor(opts.minChoice)));
  g.set("maxChoice", opts.maxChoice === null ? null : Math.max(0, Math.floor(opts.maxChoice)));
  g.set("createdAt", Date.now());
  g.set("createdBy", userId);
  doc.commit();
  return id;
}

function group(doc: LoroDoc, treeId: TreeID, groupId: string): LoroMap | null {
  const g = groupsMap(doc, treeId, false)?.get(groupId);
  return g instanceof LoroMap ? g : null;
}

function removeValue(l: LoroMovableList, value: string): void {
  for (let i = l.length - 1; i >= 0; i--) if (l.get(i) === value) l.delete(i, 1);
}

/**
 * Select or deselect one option. In a group allowing only one choice, selecting replaces this
 * client's view of the choice (other chosen ids are removed); a concurrent choice elsewhere still
 * survives the merge, which is the conflict SHACL reports.
 */
export function setChosen(doc: LoroDoc, treeId: TreeID, groupId: string, linkId: string, on: boolean): void {
  const g = group(doc, treeId, groupId);
  if (!g) return;
  const chosen = list(g, "chosen");
  if (on) {
    if (g.get("maxChoice") === 1) for (const x of chosen.toArray()) if (x !== linkId) removeValue(chosen, String(x));
    if (!chosen.toArray().includes(linkId)) chosen.push(linkId);
  } else removeValue(chosen, linkId);
  doc.commit();
}

export function addMember(doc: LoroDoc, treeId: TreeID, groupId: string, linkId: string): void {
  const g = group(doc, treeId, groupId);
  if (!g) return;
  const m = list(g, "members");
  if (!m.toArray().includes(linkId)) m.push(linkId);
  doc.commit();
}

/** Takes the option out of the group (and out of its choice); the link itself stays. */
export function removeMember(doc: LoroDoc, treeId: TreeID, groupId: string, linkId: string): void {
  const g = group(doc, treeId, groupId);
  if (!g) return;
  removeValue(list(g, "members"), linkId);
  removeValue(list(g, "chosen"), linkId);
  doc.commit();
}

/** Deletes the group only, never its links (spec §8.11). */
export function dissolveGroup(doc: LoroDoc, treeId: TreeID, groupId: string): void {
  groupsMap(doc, treeId, false)?.delete(groupId);
  doc.commit();
}
